import {
  PaymentProcessor,
  PaymentProcessorAPI,
  PaymentProcessorOptions,
  PaymentProcessorEncryptor
} from './PaymentProcessor.js'

export enum StripePaymentProcessorTypes {
  STRIPE = 'STRIPE',
  STRIPE_INTENTS = 'STRIPE_INTENTS', // this is "Stripe Standard Connect"
  STRIPE_CUSTOM_CONNECT = 'STRIPE_CUSTOM_CONNECT',
}

interface StripePaymentProcessorAPI extends PaymentProcessorAPI {
  // add methods and attrs here for Stripe
  elements: (options: { clientSecret: string } | undefined) => any
  paymentRequest: (options: StripePaymentRequest) => any
  retrievePaymentIntent: (clientSecret: string) => Promise<any>
  createToken: (tokenElement: any, options: { [key: string]: any }) => Promise<StripePaymentProcessorEncryptor>
  createPaymentMethod: (type: string, options: any) => Promise<StripePaymentProcessorEncryptor>
  handleCardAction: (clientSecret: string) => Promise<any>
}

interface StripePaymentProcessorEncryptor extends PaymentProcessorEncryptor {

}

interface StripePaymentProcessorOptions extends PaymentProcessorOptions {
  stripeAccount?: string
  apiVersion?: string
  locale?: string
}

interface StripePaymentRequest {
  country: string
  currency: string
  total: {
    label: string
    amount: number
  }
  requestPayerName: boolean
  requestPayerEmail: boolean
}

export interface StripePaymentInput {
  message?: string

  order?: {
    id: string
  }

  requiresAction?: {
    clientSecret: string
  }
}

export interface StripeCardActionResult {
  paymentIntent?: any
  error?: string
}

export enum StripePaymentMethodTypes {
  CARD = 'card'
}

export enum StripePaymentErrors {
  PAYMENT_DECLINED = 'payment_declined',
  SCA_ERROR = 'sca_error',
  SCA_DENIED = 'sca_denied',
  NO_ORDER = 'no_order'
}

function errorWithCause (message: string, cause: unknown): Error {
  return Object.assign(new Error(message), { cause })
}

export enum StripePaymentElementTypes {
  PAYMENT_REQUEST_BUTTON = 'paymentRequestButton',
  PAYMENT = 'payment',
  CARD_NUMBER = 'cardNumber',
  CARD_EXPIRY = 'cardExpiry',
  CARD_CVC = 'cardCvc',
  CARD_POSTAL_CODE = 'postalCode'
}

export interface StripePaymentElement {
  type: StripePaymentElementTypes
  nodeName: string
  params?: { [key: string]: any },
  classes?: { [key: string]: string },
  style?: { [key: string]: any },
  onCreate?: (element: any) => void
  onClick?: (event: any) => void
  onChange?: (state: any) => void
  onReady?: (event: any) => void
}

export const STRIPE_API_SCRIPT_URL = 'https://js.stripe.com/v3/'

export class StripePaymentProcessor extends PaymentProcessor {
  isInitializing: boolean = false
  initialized: boolean = false
  processorType: StripePaymentProcessorTypes = StripePaymentProcessorTypes.STRIPE_CUSTOM_CONNECT
  processorKey: string = ''
  processorOptions: StripePaymentProcessorOptions = {}
  processorAPI!: StripePaymentProcessorAPI

  public stripeElements: any

  public async fetchProcessor (
    processorType: StripePaymentProcessorTypes,
    processorKey: string,
    processorOptions: StripePaymentProcessorOptions,
    onProcessorLoad: () => void
  ): Promise<StripePaymentProcessor> {
    this.processorType = processorType
    this.processorKey = processorKey
    this.processorOptions = processorOptions
    this.onProcessorLoad = onProcessorLoad ?? (() => {})
    this.initialize()

    try {
      await this.waitUntilReady()
    } catch (_error) {
      throw new Error('StripePaymentProcessor::fetchProcessor(): Failed to initialize Stripe')
    }

    return this
  }

  initialize (): void {
    this.isInitializing = true

    // @ts-ignore
    const windowStripe = window.Stripe
    const stripeScript = document.querySelector(`script[src=""]`)

    // if Stripe is already loaded, we can initialize the client instance immediately
    if (this.initialized || this.processorAPI || stripeScript || windowStripe) {
      this.onScriptLoad()
      return
    }

    const script = document.createElement('script')
    script.setAttribute('async', '')
    script.src = STRIPE_API_SCRIPT_URL
    script.onload = () => this.onScriptLoad()

    try {
      document.head.appendChild(script)
    } catch (error) {
      throw new Error('StripePaymentProcessor: Stripe script cannot be appended to document.head')
    }
  }

  onScriptLoad (): void {
    // @ts-ignore
    this.processorAPI = window.Stripe(this.processorKey, this.processorOptions)

    this.isInitializing = false
    this.initialized = true

    // external onLoad method call after Stripe is initialized
    this.onProcessorLoad?.()
  }

  public setupUIElements (elementOptions?: { clientSecret: string }): void {
    this.stripeElements = this.processorAPI.elements(elementOptions)
  }

  /**
   * creates a stripe elements and returns it
   * see https://docs.stripe.com/js/elements_object/create_element?type=card to see the available element.params
   *
   * NOTE: debugger on event methods are not actually stopping execution more than once per event
   * try console.log instead if you need to debug more than one call of the same event
   * @param element: StripePaymentElement
   */
  public createUIElement (element: StripePaymentElement): any {
    // check that element.type is between our allowed types
    if (!Object.values(StripePaymentElementTypes).includes(element.type)) {
      throw new Error(`StripePaymentProcessor: Invalid Stripe element type: ${element.type}`)
    }

    if (!this.stripeElements) {
      this.setupUIElements()
    }

    const uiElement = this.stripeElements.create(element.type, {
      style: element.style ?? {},
      classes: element.classes ?? {},
      ...(element.params ?? {})
    })

    uiElement.node = element.nodeName

    if (element.onCreate) {
      element.onCreate(uiElement)
    }

    if (element.onClick) {
      uiElement.on('click', (event: Event) => element.onClick?.(event))
    }

    if (element.onChange) {
      uiElement.on('change', (state: any) => element.onChange?.(state))
    }

    uiElement.on('ready', (_event: any) => {
      element.onReady?.(uiElement)
    })

    const uiElementDOM = document.querySelector(element.nodeName)

    if (uiElementDOM) {
      uiElement.mount(element.nodeName)
    }

    return uiElement
  }

  public retrievePaymentIntent (clientSecret: string): Promise<any> {
    return this.processorAPI.retrievePaymentIntent(clientSecret)
  }

  public createPaymentRequest (options: StripePaymentRequest): any {
    return this.processorAPI.paymentRequest(options)
  }

  /**
   * probably deprecated, this gets a tok_[string] token from stripe
   * we should rely on "createPaymentMethod()" instead, this is here for spike/PoC reasons
   * @param tokenInput: string
   * @param options: {}
   */
  public getToken (tokenInput: string, options: any): Promise<StripePaymentProcessorEncryptor> {
    return this.processorAPI.createToken(tokenInput, options)
  }

  /**
   * converts a payment method data into a PaymentMethod Stripe object
   * see https://docs.stripe.com/js/payment_methods/create_payment_method for more details
   * @param type: StripePaymentMethodTypes
   * @param options: any
   */
  public createPaymentMethod (type: StripePaymentMethodTypes, options: any): Promise<any> {
    return this.processorAPI.createPaymentMethod(type, options)
  }

  /**
   * handles SCA (Strong Customer Authentication) for 3D Secure 2
   * it tells Stripe that it needs to display SCA UIs on screen, so user can authorize ongoing payment
   * see https://docs.stripe.com/js/payment_intents/handle_card_action for more details
   * @param clientSecret: string
   */
  public handleCardAction (clientSecret: string): Promise<any> {
    return this.processorAPI.handleCardAction(clientSecret)
  }

  /**
   * handles any generic payment response from our API
   * @param paymentResponse: StripePaymentInput
   */
  public async handlePaymentResponse (paymentResponse: StripePaymentInput): Promise<{
    stripeCardActionResult?: StripeCardActionResult,
    order?: any
  }> {
    const {
      message,
      requiresAction,
      order
    } = paymentResponse

    // SCA = strong customer authentication
    // see https://stripe.com/guides/3d-secure-2
    // sca test card: https://docs.stripe.com/testing?testing-method=card-numbers#regulatory-cards
    if (requiresAction?.clientSecret) {
      try {
        const stripeCardActionResult = await this.handleCardAction(requiresAction?.clientSecret)

        if (stripeCardActionResult.error) {
          throw errorWithCause(StripePaymentErrors.SCA_DENIED, stripeCardActionResult.error)
        }

        if (!stripeCardActionResult?.paymentIntent) {
          throw new Error(StripePaymentErrors.SCA_ERROR)
        }

        return { stripeCardActionResult }
      } catch (error: any) {
        if (error.message === StripePaymentErrors.SCA_DENIED || error.message === StripePaymentErrors.SCA_ERROR) {
          throw error
        }

        throw errorWithCause(StripePaymentErrors.SCA_ERROR, error)
      }
    }

    // if there's a message, there's an error, so we throw it back to the caller
    if (message) {
      throw new Error(message)
    }

    // if there is no order, payment was not successfull
    // NOTE: this could vary from payment endpoint to a different one, check if this condition also
    // makes sense for your use case, if you expect to receive a booking for example
    // you will need to extend this conditional to check for booking and avoid throwing a false error
    if (!order) {
      throw new Error(StripePaymentErrors.NO_ORDER)
    }

    return { order }
  }
}
